import {
  ElementProps,
  Button as RawButton,
  ButtonProps as RawButtonProps,
  useMatches,
} from '@mantine/core';
import colors, { ColorVariantType } from 'common/constants/colors.styles';
import React from 'react';
import { PredefinedFirst } from 'types/helper.type';

export type MantineButtonSize = RawButtonProps['size'];

export interface ButtonProps
  extends Omit<RawButtonProps, 'color'>,
    ElementProps<'button', keyof RawButtonProps> {
  isResponsive?: boolean;
  color?: PredefinedFirst<ColorVariantType>;
}

function responsiveVariant(variant: MantineButtonSize = 'sm'): {
  xl: MantineButtonSize;
  lg: MantineButtonSize;
  md: MantineButtonSize;
  sm: MantineButtonSize;
  xs: MantineButtonSize;
  base: MantineButtonSize;
} {
  const sizes = ['xs', 'sm', 'md', 'lg', 'xl'];

  const compactSizes = [
    'compact-xs',
    'compact-sm',
    'compact-md',
    'compact-lg',
    'compact-xl',
  ];

  let index = sizes.indexOf(variant);
  let value = sizes[index];
  let fallback = sizes[index - 1] || sizes[index];

  if (variant.includes('compact')) {
    index = compactSizes.indexOf(variant);
    value = compactSizes[index];
    fallback = compactSizes[index - 1] || compactSizes[index];
  }

  if (index === -1) {
    return {
      xl: variant,
      lg: variant,
      md: variant,
      sm: variant,
      xs: variant,
      base: variant,
    };
  }

  return {
    xl: value,
    lg: value,
    md: value,
    sm: fallback,
    xs: fallback,
    base: fallback,
  };
}

export default function Button(props: ButtonProps) {
  const {
    type = 'button',
    size: _size = 'xs',
    isResponsive = true,
    color: _color = 'blue',
    radius = 8,
    ...rest
  } = props;

  const size = useMatches(responsiveVariant(_size)) as RawButtonProps['size'];

  const color = React.useMemo(() => {
    return colors?.[_color] || _color;
  }, [_color]);

  return (
    <RawButton
      type={type}
      size={isResponsive ? size : _size}
      color={color}
      radius={radius}
      {...rest}
    />
  );
}
